import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { CitationContentType, PromptExecutionSettingsDto } from '../../api-service/clients';
import { CitationSourceType, IChatMessage, useChat } from '../../hooks/ai-search/use-chat';
import { ChatMessages } from './chat-messages';
import { ChatMessageInput } from './chat-message-input';
import { AssistantType } from './assistant-type';
import { useChatErrorHandling } from './hooks/use-chat-error-handling';
import { chatComplianceBoxPropsLexi } from './chat-compliance-box-props-lexi';
import { Checkbox } from '../checkbox';
import { moduleClientFactory } from '../../api-service/module-client-factory';

export interface IFasitAssistantChatboxProps {
  settings: PromptExecutionSettingsDto;
  onSettingsChange: (property: keyof PromptExecutionSettingsDto, value: any) => void;
}

export function FasitAssistantChatbox(props: IFasitAssistantChatboxProps) {
  const { settings, onSettingsChange } = props;
  const { messages, handlePrompt, isLoading, error, setChatFeedback, citationsTypes, setCitationsTypes, clearChat } = useChat(AssistantType.Fasit);

  useChatErrorHandling(error);

  const client = moduleClientFactory.createAiSearchClient();

  const [availableCitationSourceTypes, setAvailableCitationSourceTypes] = useState<CitationSourceType[]>([CitationSourceType.FasitGuides, CitationSourceType.LegalDocuments]);

  useEffect(() => {
    client.getAvailableCitationContentTypesForFasitAssistant().then(response => {
      if (response.availableContentTypes) {
        const mappedCitationTypes = response.availableContentTypes?.map(mapCitationContentTypeToCitationSourceType);
        setAvailableCitationSourceTypes(mappedCitationTypes);
      }
    });
  }, []);

  function handleSubmitPrompt(inputString: string, standardQuestionId?: string) {
    handlePrompt({ content: inputString.trim(), contentTypes: citationsTypes.map(mapCitationSourceTypeToCitationContentType), customPromptSettings: settings, standardQuestionId });
  }

  async function handleChatFeedbackChange(msg: IChatMessage) {
    await setChatFeedback(msg);
  }

  const handleDataSourceChange = (type: CitationSourceType) => {
    if (citationsTypes.find(x => x === type) !== undefined) {
      setCitationsTypes(citationsTypes.filter(x => x !== type));
    } else {
      setCitationsTypes([...citationsTypes, type]);
    }
  };

  function mapCitationSourceTypeToCitationContentType(sourceType: CitationSourceType): CitationContentType {
    switch (sourceType) {
      case CitationSourceType.FasitGuides:
        return CitationContentType.UserGuide;
      case CitationSourceType.LegalDocuments:
        return CitationContentType.LegalDocument;
      case CitationSourceType.MunicipalityAssistantDocuments:
        return CitationContentType.MunicipalityAssistantDocuments;
      default:
        throw new Error(`Unknown CitationSourceType: ${sourceType}`);
    }
  }

  function mapCitationContentTypeToCitationSourceType(contentType: CitationContentType): CitationSourceType {
    switch (contentType) {
      case CitationContentType.UserGuide:
        return CitationSourceType.FasitGuides;
      case CitationContentType.LegalDocument:
        return CitationSourceType.LegalDocuments;
      case CitationContentType.MunicipalityAssistantDocuments:
        return CitationSourceType.MunicipalityAssistantDocuments;
      default:
        throw new Error(`Unknown CitationContentType: ${contentType}`);
    }
  }

  const DataSourcesComponent = useMemo(
    () => (
      <>
        <Checkbox
          label="Fasit vejledninger"
          checked={citationsTypes.includes(CitationSourceType.FasitGuides)}
          onChange={() => handleDataSourceChange(CitationSourceType.FasitGuides)}
          disabled={!availableCitationSourceTypes.includes(CitationSourceType.FasitGuides)}
        />
        <Checkbox
          label="Egne dokumenter"
          checked={citationsTypes.includes(CitationSourceType.MunicipalityAssistantDocuments)}
          onChange={() => handleDataSourceChange(CitationSourceType.MunicipalityAssistantDocuments)}
          disabled={!availableCitationSourceTypes.includes(CitationSourceType.MunicipalityAssistantDocuments)}
        />
        <Checkbox
          label="Lovtekster"
          checked={citationsTypes.includes(CitationSourceType.LegalDocuments)}
          onChange={() => handleDataSourceChange(CitationSourceType.LegalDocuments)}
          disabled={!availableCitationSourceTypes.includes(CitationSourceType.LegalDocuments)}
        />
      </>
    ),
    [citationsTypes, availableCitationSourceTypes]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', position: 'absolute', width: '100%', height: '-webkit-fill-available' }}>
      <ChatMessages isLoading={isLoading} messages={messages} onChatFeedbackChange={handleChatFeedbackChange} chatComplianceBoxProps={chatComplianceBoxPropsLexi} />
      <ChatMessageInput
        isLoading={isLoading}
        onClearChat={clearChat}
        onSettingsChange={onSettingsChange}
        onSubmitPrompt={handleSubmitPrompt}
        settings={settings}
        assistantType={AssistantType.Fasit}
        dataSourcesComponent={DataSourcesComponent}
      />
    </Box>
  );
}
